import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import LookupSelect from "@/components/forms/LookupSelect";
import CheckboxGroup from "@/components/forms/CheckboxGroup";
import { useLookups } from "@/contexts/LookupsContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const MAX_OFFER_PHOTOS = 5;

const EMPTY_OFFER = {
  country: "", region: "", appellation: "", positions_count: 1, date_start: "", date_end: "",
  salary_amount: "", salary_period: "hourly", housing: "not_provided", meals: "not_included",
  mission_type: "harvest", duration: "1_2_weeks", skills_wanted: [], permits_wanted: [],
  languages_wanted: [], additional_info: "", photos: [],
};

export default function OfferFormPage({ offerId: offerIdProp, producerId, onSaved }) {
  const { t } = useTranslation();
  const { id: idParam } = useParams();
  const id = offerIdProp !== undefined ? offerIdProp : idParam;
  const navigate = useNavigate();
  const lookups = useLookups();
  const [offer, setOffer] = useState(EMPTY_OFFER);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const isEmbedded = !!onSaved;

  useEffect(() => {
    if (id) {
      api.get(`/offers/${id}`).then(({ data }) => setOffer(data));
    }
  }, [id]);

  const update = (field, value) => setOffer((o) => ({ ...o, [field]: value }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/offers/${id}/photos`, formData);
      setOffer((o) => ({ ...o, photos: data.photos }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, t));
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const handlePhotoDelete = async (path) => {
    try {
      const { data } = await api.delete(`/offers/${id}/photos`, { params: { path } });
      setOffer((o) => ({ ...o, photos: data.photos }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, t));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...offer, positions_count: Number(offer.positions_count), salary_amount: Number(offer.salary_amount) };
      delete payload.photos;
      if (id) {
        await api.put(`/offers/${id}`, payload);
        toast.success(t("offer.offerPublished"));
        if (onSaved) onSaved(id);
        else navigate("/dashboard/producer/offers");
      } else {
        const createUrl = producerId ? "/admin/offers" : "/offers";
        const createPayload = producerId ? { ...payload, producer_id: producerId } : payload;
        const { data } = await api.post(createUrl, createPayload);
        toast.success(t("offer.offerPublished"));
        if (onSaved) onSaved(data.id);
        else navigate(`/dashboard/producer/offers/${data.id}/edit`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setSaving(false);
    }
  };

  if (!lookups) return null;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-8 bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 sm:p-8" data-testid="offer-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="mb-1.5 block">{t("offer.country")}</Label>
            <LookupSelect options={lookups.countries} value={offer.country} onChange={(v) => update("country", v)}
                          labelPrefix="lookups.countries" testId="offer-form-country-select" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("offer.region")}</Label>
            <LookupSelect options={lookups.regions_by_country[offer.country] || []} value={offer.region} onChange={(v) => update("region", v)}
                          labelPrefix="lookups.regions" testId="offer-form-region-select" />
          </div>
          <div className="sm:col-span-2">
            <Label>{t("offer.appellation")}</Label>
            <Input className="h-12 rounded-xl mt-1.5" value={offer.appellation || ""} onChange={(e) => update("appellation", e.target.value)} data-testid="offer-form-appellation-input" />
          </div>
          <div>
            <Label>{t("offer.positionsCount")}</Label>
            <Input type="number" min={1} className="h-12 rounded-xl mt-1.5" value={offer.positions_count} onChange={(e) => update("positions_count", e.target.value)} data-testid="offer-form-positions-count-input" />
          </div>
          <div />
          <div>
            <Label>{t("offer.dateStart")}</Label>
            <Input type="date" required className="h-12 rounded-xl mt-1.5" value={offer.date_start} onChange={(e) => update("date_start", e.target.value)} data-testid="offer-form-date-start-input" />
          </div>
          <div>
            <Label>{t("offer.dateEnd")}</Label>
            <Input type="date" required className="h-12 rounded-xl mt-1.5" value={offer.date_end} onChange={(e) => update("date_end", e.target.value)} data-testid="offer-form-date-end-input" />
          </div>
          <div>
            <Label>{t("offer.salaryAmount")}</Label>
            <Input type="number" step="0.01" required className="h-12 rounded-xl mt-1.5" value={offer.salary_amount} onChange={(e) => update("salary_amount", e.target.value)} data-testid="offer-form-salary-amount-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("offer.salaryPeriod")}</Label>
            <LookupSelect options={lookups.salary_periods} value={offer.salary_period} onChange={(v) => update("salary_period", v)}
                          labelPrefix="lookups.salaryPeriods" testId="offer-form-salary-period-select" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("offer.housing")}</Label>
            <LookupSelect options={lookups.housing_options} value={offer.housing} onChange={(v) => update("housing", v)}
                          labelPrefix="lookups.housingOptions" testId="offer-form-housing-select" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("offer.meals")}</Label>
            <LookupSelect options={lookups.meals_options} value={offer.meals} onChange={(v) => update("meals", v)}
                          labelPrefix="lookups.mealsOptions" testId="offer-form-meals-select" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("offer.missionType")}</Label>
            <LookupSelect options={lookups.mission_types} value={offer.mission_type} onChange={(v) => update("mission_type", v)}
                          labelPrefix="lookups.missionTypes" testId="offer-form-mission-type-select" />
          </div>
          <div>
            <Label className="mb-1.5 block">{t("offer.duration")}</Label>
            <LookupSelect options={lookups.durations} value={offer.duration} onChange={(v) => update("duration", v)}
                          labelPrefix="lookups.durations" testId="offer-form-duration-select" />
          </div>
        </div>

        <div>
          <Label className="mb-2 block font-semibold">{t("offer.skillsWanted")}</Label>
          <CheckboxGroup options={lookups.skills} value={offer.skills_wanted} onChange={(v) => update("skills_wanted", v)}
                         labelPrefix="lookups.skills" testIdPrefix="offer-form-skill" />
        </div>
        <div>
          <Label className="mb-2 block font-semibold">{t("offer.permitsWanted")}</Label>
          <CheckboxGroup options={lookups.permits} value={offer.permits_wanted} onChange={(v) => update("permits_wanted", v)}
                         labelPrefix="lookups.permits" testIdPrefix="offer-form-permit" />
        </div>
        <div>
          <Label className="mb-2 block font-semibold">{t("offer.languagesWanted")}</Label>
          <CheckboxGroup options={lookups.languages} value={offer.languages_wanted} onChange={(v) => update("languages_wanted", v)}
                         labelPrefix="lookups.languages" testIdPrefix="offer-form-language" />
        </div>
        <div>
          <Label>{t("offer.additionalInfo")}</Label>
          <Textarea rows={3} className="rounded-xl mt-1.5" value={offer.additional_info} onChange={(e) => update("additional_info", e.target.value)} data-testid="offer-form-additional-info-textarea" />
        </div>

        {id && (
          <div>
            <Label className="mb-2 block font-semibold">{t("offer.photosSection")}</Label>
            <p className="text-xs text-[#2B2B2B]/50 mb-3">{t("offer.photosSectionDesc", { max: MAX_OFFER_PHOTOS })}</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="offer-form-photos-grid">
              {(offer.photos || []).map((path) => (
                <div key={path} className="relative aspect-square rounded-xl overflow-hidden border border-black/10 group">
                  <img src={`${API}/profiles/files/${path}`} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => handlePhotoDelete(path)} data-testid={`offer-form-photo-delete-${path.split("/").pop()}`}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {(offer.photos || []).length < MAX_OFFER_PHOTOS && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-black/15 flex items-center justify-center cursor-pointer hover:border-[#4ba3b7] transition-colors">
                  {uploadingPhoto ? <Loader2 className="h-5 w-5 animate-spin text-[#4ba3b7]" /> : <Camera className="h-5 w-5 text-[#2B2B2B]/40" />}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handlePhotoUpload}
                         disabled={uploadingPhoto} data-testid="offer-form-photo-upload-input" />
                </label>
              )}
            </div>
          </div>
        )}

        <Button type="submit" disabled={saving} className="w-full h-12 rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] font-bold" data-testid="offer-form-submit-button">
          {saving ? t("common.loading") : t("offer.publishFree")}
        </Button>
      </form>
  );

  if (isEmbedded) return formContent;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10 sm:py-14">
      <h1 className="font-heading text-2xl font-bold mb-8">{id ? t("offer.editOffer") : t("offer.createOffer")}</h1>
      {formContent}
    </div>
  );
}
